import { Chip, Skeleton, Stack, Typography } from '@mui/joy';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { IConsumerRestartTarget } from 'dashboard/types';
import { Color, Direction, Level, Size, SkeletonVariant, Variant } from 'theme/enums';

import { isDevPoolEnv } from './utils';

interface IProps {
  cooldownEnvs: string[];
  isLoading: boolean;
  onRestart: (target: IConsumerRestartTarget) => void;
  restartingEnv: string | null;
  targets: IConsumerRestartTarget[];
}

const SKELETON_COUNT = 6;

export const RestartButtons = ({
  cooldownEnvs,
  isLoading,
  onRestart,
  restartingEnv,
  targets,
}: IProps) => {
  const { t } = useTranslation();

  // The instance actually being viewed, so the one button that is safe by
  // definition is easy to pick out of two dozen.
  const currentEnv = window.location.hostname;

  const getTooltipTitle = (target: IConsumerRestartTarget) => {
    if (cooldownEnvs.includes(target.env)) {
      return t('tooltipRestartConsumersCooldown');
    }

    // Every other button greys out while one restart runs; without this they would
    // go dead with no stated reason.
    if (null !== restartingEnv && restartingEnv !== target.env) {
      return t('tooltipRestartConsumersBusy', { env: restartingEnv.toUpperCase() });
    }

    if (target.env === currentEnv) {
      return t('tooltipRestartConsumersCurrent', { env: target.label.toUpperCase() });
    }

    return t('tooltipRestartConsumers', { env: target.label.toUpperCase() });
  };

  const renderGroup = (label: string, groupTargets: IConsumerRestartTarget[], color: Color) => {
    if (!groupTargets.length) {
      return null;
    }

    return (
      <Stack spacing={1}>
        <Typography level={Level.TITLE_SM} sx={{ color: 'text.tertiary' }}>
          {label}
        </Typography>

        <Stack direction={Direction.ROW} flexWrap="wrap" spacing={1} useFlexGap>
          {groupTargets.map((target) => {
            const isCurrent = target.env === currentEnv;
            const isHealthy = target.consumers >= target.expectedConsumers;
            const isSurplus = target.consumers > target.expectedConsumers;

            // Just restarted: a second run would overshoot the consumer count,
            // because a consumer caught mid-shutdown respawns itself with --force
            // and nothing reaps the surplus.
            const isCoolingDown = cooldownEnvs.includes(target.env);

            return (
              <Button
                color={isCurrent ? Color.PRIMARY : color}
                disabled={isCoolingDown || (null !== restartingEnv && restartingEnv !== target.env)}
                endDecorator={
                  <Chip
                    // Colour is a shortcut, not the message: the count is spelled out
                    // for assistive tech because red-vs-neutral is the only other cue.
                    aria-label={t('labelConsumerHealth', {
                      count: target.consumers,
                      expected: target.expectedConsumers,
                    })}
                    color={isHealthy ? Color.NEUTRAL : Color.DANGER}
                    size={Size.SM}
                    // A surplus is not a fault, but it is an anomaly this page exists
                    // to surface, so it is outlined rather than styled as normal.
                    variant={isSurplus ? Variant.OUTLINED : Variant.SOFT}
                  >
                    {`${target.consumers}/${target.expectedConsumers}`}
                  </Chip>
                }
                key={target.env}
                loading={restartingEnv === target.env}
                onClick={() => onRestart(target)}
                size={Size.SM}
                // A bare env name reads as a label or a filter, not something that
                // acts. The icon, the tooltip and the confirmation dialog each say
                // "restart" so the action cannot be mistaken for navigation.
                startDecorator={<ArrowsClockwise />}
                text={target.label.toUpperCase()}
                tooltipProps={{ children: null, title: getTooltipTitle(target) }}
                variant={isCurrent ? Variant.SOLID : Variant.OUTLINED}
              />
            );
          })}
        </Stack>
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Stack direction={Direction.ROW} flexWrap="wrap" spacing={1} useFlexGap>
        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <Skeleton
            key={index}
            sx={{ borderRadius: Size.SM, height: 32, position: 'relative', width: 120 }}
            variant={SkeletonVariant.RECTANGULAR}
          />
        ))}
      </Stack>
    );
  }

  // No configured targets means this deployment never opted in — prod ships that
  // way. The buttons are hidden entirely rather than rendered empty; the note is
  // here so an admin landing on the page knows why this card is bare.
  if (!targets.length) {
    return (
      <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
        {t('messageConsumerRestartNoTargets')}
      </Typography>
    );
  }

  const devPool = targets.filter(({ env }) => isDevPoolEnv(env));
  const shared = targets.filter(({ env }) => !isDevPoolEnv(env));

  return (
    <Stack spacing={2}>
      {renderGroup(t('labelSharedEnvironments'), shared, Color.WARNING)}

      {renderGroup(t('labelDevEnvironments'), devPool, Color.NEUTRAL)}
    </Stack>
  );
};
